import type {
  AgentRunDetailResponse,
  MatchSide,
  MatchSideComparison,
} from '~/entities/document-agent'
import {
  agentMatchAgreement,
  type Agreement,
  buildCodeRows,
  type CategoryPartKey,
  type CategoryParts,
  type CodeRow,
  type CodeTally,
  combineAgreements,
  compareCategory,
  compareCodes,
  comparePartsToFinal,
  getAgentCategoryParts,
  isReviewed,
  productionMatchAgreement,
  tallyCodes,
} from './agreement'

export type CategoryInfo = {
  slug: string | null
  label: string | null
  description: string | null
  parts: CategoryParts | null
}

export type CategoryDescriber = (slug: string | null) => CategoryInfo

export type ComparedCategory = CategoryInfo & {
  agreement: Agreement
  partAgreements: Record<CategoryPartKey, Agreement>
}

export type ComparedCodes = { tally: CodeTally; agreement: Agreement }

export type MatchReview = {
  status: string
  /** The side the document is scored on, when it is scored. */
  side: MatchSide | null
  sideData: MatchSideComparison | null
  excluded: string | null
  agent: Agreement
  today: Agreement
}

/** Everything the page says about how the run compares, worked out once. */
export type RunReview = {
  reviewed: boolean
  category: { final: CategoryInfo; agent: ComparedCategory; today: ComparedCategory }
  codes: { rows: CodeRow[]; hasFinal: boolean; agent: ComparedCodes; today: ComparedCodes }
  match: MatchReview | null
  overall: Agreement
  overallToday: Agreement
}

export function buildReview(
  { run, comparison }: Pick<AgentRunDetailResponse, 'run' | 'comparison'>,
  describeCategory: CategoryDescriber,
): RunReview {
  const reviewed = isReviewed(comparison)
  const final = describeCategory(comparison?.final_category ?? null)
  const agentInfo = describeCategory(run.agent_category)
  const agentParts = getAgentCategoryParts(run) ?? agentInfo.parts
  const today = describeCategory(comparison?.today_category ?? null)

  const compared = (info: CategoryInfo, parts: CategoryParts | null): ComparedCategory => ({
    ...info,
    parts,
    agreement: compareCategory({
      value: info.slug,
      parts,
      final: final.slug,
      finalParts: final.parts,
      reviewed,
    }),
    partAgreements: comparePartsToFinal(parts, final.parts, reviewed),
  })

  const finalCodes = comparison?.reviewer_tags ?? null
  // Tag reviews only come from people, and only count once the document is reviewed.
  const hasFinal = reviewed && finalCodes !== null
  const rows = buildCodeRows(
    hasFinal ? finalCodes : null,
    comparison?.agent_tags ?? [],
    comparison?.today_tags ?? [],
  )
  const codesOf = (who: 'agent' | 'today'): ComparedCodes => {
    const tally = tallyCodes(rows, who)
    return { tally, agreement: compareCodes(tally, hasFinal) }
  }
  const hasAgentResult = !!run.final

  const matching = comparison?.matching ?? null
  const match: MatchReview | null = run.match
    ? (() => {
        const side = matching?.excluded ? null : (matching?.final_side ?? null)
        const sideData = side ? (matching?.sides[side] ?? null) : null
        return {
          status: run.match.status,
          side,
          sideData,
          excluded: matching?.excluded ?? null,
          agent: agentMatchAgreement(sideData?.verdict),
          today: productionMatchAgreement(sideData?.reviewer?.outcome),
        }
      })()
    : null

  const category = {
    final,
    agent: hasAgentResult
      ? compared(agentInfo, agentParts)
      : { ...compared(agentInfo, agentParts), agreement: 'none' as const },
    today: compared(today, today.parts),
  }
  const codes = {
    rows,
    hasFinal,
    agent: hasAgentResult
      ? codesOf('agent')
      : { tally: tallyCodes(rows, 'agent'), agreement: 'none' as const },
    today: codesOf('today'),
  }

  return {
    reviewed,
    category,
    codes,
    match,
    overall: combineAgreements([
      category.agent.agreement,
      codes.agent.agreement,
      match?.agent ?? 'none',
    ]),
    overallToday: combineAgreements([
      category.today.agreement,
      codes.today.agreement,
      match?.today ?? 'none',
    ]),
  }
}

const RANK: Partial<Record<Agreement, number>> = { agree: 2, partial: 1, differ: 0 }

/** Where the agent did better, the same or worse than production, over the checks both have. */
export function compareToProduction(pairs: Array<[Agreement, Agreement]>) {
  const tally = { better: 0, same: 0, worse: 0 }
  for (const [agent, today] of pairs) {
    const a = RANK[agent]
    const t = RANK[today]
    if (a === undefined || t === undefined) continue
    if (a > t) tally.better++
    else if (a < t) tally.worse++
    else tally.same++
  }
  return tally
}
